import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Option } from './Option/Option';
import { OptionBalance } from './Option/OptionBalance';
import cls from './Select.module.css';

export const Select = ({
    selected,
    options,
    placeholder,
    onChange,
    onClose,
    title,
    isRequired,
    isDisabled = false,
    error = null,
    isAccount = null,
    isSmartPosition = false,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [resultOptions, setResultOptions] = useState([]);
    const [optionFilter, setOptionFilter] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const rootRef = useRef(null);

    useEffect(() => {
        const handleClick = (event) => {
            const { target } = event;
            if (target instanceof Node && !rootRef.current?.contains(target)) {
                isOpen && onClose?.();
                setIsOpen(false);
                setOptionFilter('');
            }
        };
        window.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        setResultOptions(options);
    }, [options]);

    useEffect(() => {
        const calcRect = () => {
            if (!isSmartPosition) {
                setDropdownStyle({});
                return;
            }

            if (!rootRef.current) return;

            const rect = rootRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < 170;

            setDropdownStyle({
                position: 'fixed',
                top: openUpward ? rect.top - 8 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                transform: openUpward ? 'translateY(-100%)' : 'none',
            });
        };

        calcRect();

        window.addEventListener('resize', calcRect);
        window.addEventListener('scroll', calcRect, true);

        return () => {
            window.removeEventListener('resize', calcRect);
            window.removeEventListener('scroll', calcRect, true);
        };
    }, [isOpen, isSmartPosition]);

    useEffect(() => {
        const headers = options.filter((option) => option.isHead);

        if (!optionFilter) {
            setResultOptions(options);
            return;
        }
        if (!headers.length) {
            const filtered = options.filter(
                (option) =>
                    option.title.toLowerCase().includes(optionFilter.toLowerCase()) && !isDisabled
            );
            if (filtered.length) {
                setResultOptions(filtered);
            } else {
                setResultOptions([
                    {
                        title: t('Select.return.setResultOptions.notFind'),
                        value: 'null',
                        isDisabled: true,
                    },
                ]);
            }
            return;
        }
        const nullOption = options.filter((option) => !option.value);
        const allOption = options.filter((option) => option.value === 'all');
        const isHeadDisabled = headers.some((option) => option.isDisabled);

        if (!isHeadDisabled) {
            let filteredOptions = [...nullOption, ...allOption];

            headers.forEach((option) => {
                if (option.title.toLowerCase().includes(optionFilter.toLowerCase())) {
                    filteredOptions = [...filteredOptions, option, ...option.children];
                } else {
                    const filteredChildren = option.children.filter((option) =>
                        option.title.toLowerCase().includes(optionFilter.toLowerCase())
                    );
                    if (filteredChildren.length) {
                        filteredOptions = [...filteredOptions, option, ...filteredChildren];
                    }
                }
            });

            if (filteredOptions.length === 1) {
                filteredOptions = [
                    {
                        title: t('Select.return.FilteredResultOptions.notFind'),
                        value: 'null',
                        isDisabled: true,
                    },
                ];
            }

            setResultOptions(filteredOptions);
        } else {
            let filteredOptions = [...nullOption, ...allOption];
            headers.forEach((option) => {
                const filteredChildren = option.children.filter((option) =>
                    option.title.toLowerCase().includes(optionFilter.toLowerCase())
                );
                if (filteredChildren.length) {
                    filteredOptions = [...filteredOptions, option, ...filteredChildren];
                }
            });

            if (filteredOptions.length === 1) {
                filteredOptions = [
                    {
                        title: t('Select.return.FilteredResultOptions.notFind'),
                        value: 'null',
                        isDisabled: true,
                    },
                ];
            }

            setResultOptions(filteredOptions);
        }
    }, [optionFilter]);

    const handleOptionClick = (value) => {
        setIsOpen(false);
        onChange(value);
    };
    const handlePlaceHolderClick = () => {
        if (!isDisabled) {
            if (isOpen) {
                setOptionFilter('');
            }
            setIsOpen(!isOpen);
        }
    };

    const handleOptionFilter = (event) => {
        setOptionFilter(event.target.value);
    };

    return (
        <div className={classNames(cls.formBlockContent, { [cls.disabled]: isDisabled })}>
            {title && (
                <label className={classNames(cls.modalLabelSelect)}>
                    {title}
                    {isRequired && <span>*</span>}
                </label>
            )}
            <div
                onClick={handlePlaceHolderClick}
                className={classNames(cls.selectWrapper, { [cls.disabledWrapper]: isDisabled })}
                ref={rootRef}
                data-is-active={isOpen}
            >
                <div className={classNames(cls.arrow)}>
                    <svg
                        width="9"
                        height="6"
                        viewBox="0 0 9 6"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M4.5 5.5L9 0.5H0L4.5 5.5Z" fill="currentColor" fillOpacity="0.6" />
                    </svg>
                </div>
                {selected?.title ? (
                    <div className={classNames(cls.selected)}>
                        {selected.icon ? selected.icon : ''}
                        <span className={classNames(cls.selectedSpan)}>
                            {selected.title}
                            {isAccount && <OptionBalance accountBalance={selected.balance} />}
                        </span>
                    </div>
                ) : (
                    <div className={classNames(cls.placeholder)}>{placeholder}</div>
                )}
                {isOpen && (
                    <ul className={classNames(cls.option)} style={dropdownStyle}>
                        <div
                            className={classNames(cls.filterInput)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                className={classNames(cls.input)}
                                onChange={(event) => handleOptionFilter(event)}
                            />
                        </div>
                        {resultOptions.map((option) => (
                            <Option
                                key={option.value}
                                option={option}
                                onClick={handleOptionClick}
                                setIsOpen={setIsOpen}
                                isAccount={isAccount}
                            />
                        ))}
                    </ul>
                )}
            </div>
            {error && <span className={classNames(cls.errorMessage)}>{error}</span>}
        </div>
    );
};
